import { useContext } from 'react';
import { useLocation, useParams } from 'react-router';
import { store } from 'components/Store';
import { useMainActionsBarFunctions } from 'components/Dashboard/SubNav/MainListActionsBar';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';

interface UnseenKeys {
  unseenNumber: string;
  lastSeenMarker: string;
}

interface SectionResults {
  feedItemFlags?: { feedItemId: number; isBackpopulated?: boolean | null }[];
  hits?: { objectID?: string }[];
  scrollPosition?: number;
  listSection?: string;
}

interface ScrollBarRef {
  current?: { view?: { scrollTop?: number } } | null;
}

const unseenNumbers: Partial<Record<string, UnseenKeys>> = {
  KeyUpdates: { unseenNumber: 'KeyUpdate', lastSeenMarker: 'keyUpdateFeedItemId' },
  Consultations: { unseenNumber: 'Consultation', lastSeenMarker: 'consultationFeedItemId' },
  Legislation: { unseenNumber: 'Legislation', lastSeenMarker: 'legislationFeedItemId' },
  ParliamentLive: { unseenNumber: 'ParliamentLive', lastSeenMarker: 'parliamentLiveFeedItemId' },
  ParliamentaryRecord: { unseenNumber: 'ParliamentaryRecord', lastSeenMarker: 'parliamentaryRecordFeedItemId' },
  CommitteePublications: { unseenNumber: 'CommitteePublications', lastSeenMarker: 'committeePublicationFeedItemId' },
  LibraryMaterial: { unseenNumber: 'CommonsLibraryArticle', lastSeenMarker: 'commonsLibraryArticleFeedItemId' },
  GovernmentPublications: {
    unseenNumber: 'GovernmentPublication',
    lastSeenMarker: 'governmentPublicationFeedItemId',
  },
  SocialMedia: { unseenNumber: 'Tweet', lastSeenMarker: 'tweetFeedItemId' },
  Influence: { unseenNumber: 'UserContent', lastSeenMarker: 'userContentFeedItemId' },
  UpcomingEvents: { unseenNumber: 'Event', lastSeenMarker: 'eventFeedItemId' },
  Regulations: { unseenNumber: 'RegulationDocument', lastSeenMarker: 'regulationDocumentFeedItemId' },
};

const useRememberPosition = () => {
  const globalState = useContext(store) as unknown as {
    state: { unseenNumbersCurrentList: Record<string, number>; search: { list: unknown } };
    dispatch: (action: { type: string; parameter: string; value: unknown }) => void;
  };
  const { state, dispatch } = globalState;
  const location = useLocation();
  const params = useParams();
  const { mainSection } = params;

  const { contentResults, setContentResults, lastSeenMarkers, itemsViewed, setFeedState } =
    useContentResults() as unknown as {
      contentResults: Record<string, SectionResults | undefined>;
      setContentResults: (results: Record<string, SectionResults | undefined>) => void;
      lastSeenMarkers: Record<string, number | null>;
      itemsViewed: (string | undefined)[];
      setFeedState: (field: string, value: unknown) => void;
    };
  const { unseenNumbersCurrentList, search } = state;
  const { list } = search;

  const { setTemporarySelectedUpdates } = useMainActionsBarFunctions();

  const rememberPosition = (section: string, scrollBarRef?: ScrollBarRef | null, index = -1) => {
    let currentSection = contentResults[section];
    let sectionUnseenProperties = unseenNumbers[section];
    let newSectionObject = { ...currentSection };
    const feedItem = newSectionObject.feedItemFlags?.[index];
    const isBackpopulatedCondition = feedItem?.isBackpopulated === null || feedItem?.isBackpopulated === true;
    const lastSeenMarker = sectionUnseenProperties && lastSeenMarkers[sectionUnseenProperties.lastSeenMarker];

    const unseenItem =
      !!list &&
      !!sectionUnseenProperties &&
      !!feedItem &&
      !!lastSeenMarker &&
      lastSeenMarker < feedItem.feedItemId &&
      !isBackpopulatedCondition;

    if (unseenItem) {
      feedItem.feedItemId = -1;
    }

    if (scrollBarRef?.current) {
      newSectionObject = {
        ...newSectionObject,
        scrollPosition: scrollBarRef?.current?.view?.scrollTop ?? 0,
        listSection: mainSection,
      };
    }

    let newResults = {
      ...contentResults,
      [section]: newSectionObject,
    };
    if (unseenItem) {
      const { unseenNumber } = sectionUnseenProperties;
      let newUnseenNumbers = { ...unseenNumbersCurrentList };
      newUnseenNumbers[unseenNumber] = (newUnseenNumbers[unseenNumber] ?? 0) - 1;
      let item = newSectionObject?.hits?.[index];
      let newItemsViewed = [...itemsViewed];
      if (!newItemsViewed.includes(item?.objectID)) {
        newItemsViewed.push(item?.objectID);
      }
      setFeedState('itemsViewed', newItemsViewed);
      dispatch({ type: 'MODIFY_SECTION', parameter: 'unseenNumbersCurrentList', value: newUnseenNumbers });
    }
    if (scrollBarRef) {
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'clickedOnContentUrl',
        value: `${location.pathname}${location.search}${location.hash}`,
      });
    }
    setContentResults(newResults);
    setTemporarySelectedUpdates();
  };

  return { rememberPosition };
};

export type { ScrollBarRef };
export { unseenNumbers };
export default useRememberPosition;
